import { memo, useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { AxiosResponse } from "axios";

import { StatementsList } from "components/organisms";
import { PageHeader } from "components/molecules";

import { ComplexFilterItem, ComplexFilterType } from "interfaces/filters";
import PropertyMultiSelect from "components/molecules/PropertyMultiSelect";
import PropertiesClient from "services/api/properties";
import { Property } from "models/properties";

import {
	getStatements,
	getStatementsFilter,
	setStatementsFilter,
} from "state/modules/statements";
import { defaultStatementsDateFilter } from "utils/statements";
import { FONT_FAMILY_BOLD, FONT_WEIGHT_BOLD } from "styles/typography";
import { MAIN_THEME } from "styles/colors";



const Container = styled.div`
	width: 100%;
	height: 100vh;
	overflow: auto;
	background: ${MAIN_THEME.DARK_BG};

	@media (max-width: 768px) {
		height: auto;
		min-height: 100vh;
	}
`;

const FiltersContainer = styled.div`
	margin-bottom: 15px;
	z-index: 10;
	position: relative;
	padding: 0 20px;

	@media (max-width: 768px) {
		padding: 0 15px;
	}

	@media (max-width: 480px) {
		padding: 0 10px;
	}
`;

const StatementsPage = () => {
	const filters = useSelector(getStatementsFilter);
	const [properties, setProperties] = useState<any[]>([]);
	const [selectedProperties, setSelectedProperties] = useState<any[]>([
		{ label: "All", value: "" },
	]);

	const dispatch = useDispatch();

	useEffect(() => {
		const loadProperties = async () => {
			try {
				const res = (await PropertiesClient.getProperties({
					offset: 0,
					city: "",
					name: "",
				})) as AxiosResponse;

				const propertiesList = res.data.content as Property[];

				const transformedProperties = propertiesList.map((item) => ({
					label: item.name || "-",
					value: item.id,
				}));

				setProperties(transformedProperties);
			} catch (error) {
				console.log({ error });
			}
		};

		loadProperties();
	}, []);

	useEffect(() => {
		dispatch(
			getStatements({
				isLoadMore: false,
			})
		);
	}, []);

	const handleSelectProperties = (selectedProps: any[]) => {
		setSelectedProperties(selectedProps);
		
		// If "All" is selected (empty value), ignore other selections and set to null (all properties)
		const hasAllSelected = selectedProps.some((p) => !p.value);
		
		let propertyIdValue: string | null = null;
		
		if (!hasAllSelected) {
			// Only use specific properties if "All" is not selected
			const selectedValues = selectedProps.filter((p) => p.value).map((p) => p.value);
			propertyIdValue = selectedValues.length > 0 ? selectedValues.join(",") : null;
		}
		
		const propertyFilter = {
			type: ComplexFilterType.PROPERTY_ID,
			value: propertyIdValue,
		} as ComplexFilterItem;

		// Update the filter, adding it if it doesn't exist
		const hasPropertyFilter = filters.some((f) => f.type === ComplexFilterType.PROPERTY_ID);
		const updatedFilters = hasPropertyFilter
			? filters.map((filter: ComplexFilterItem) => {
					if (filter.type === ComplexFilterType.PROPERTY_ID) {
						return propertyFilter;
					}
					return filter;
				})
			: [...filters, propertyFilter];

		dispatch(setStatementsFilter(updatedFilters));
		dispatch(getStatements({}));
	};

		return (
			<Container>
				<PageHeader
					title="Statements"
					description="Review financial statements for your properties."
				/>
				<FiltersContainer>
					<PropertyMultiSelect
						properties={properties}
						selectedProperties={selectedProperties}
						onSelect={handleSelectProperties}
					/>
				</FiltersContainer>
				<StatementsList showProperty />
			</Container>
		);
};

export default memo(StatementsPage);
